import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";

const CartContext = createContext();

export const useCartContext = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(
    JSON.parse(localStorage.getItem("cartItems")) || []
  );
  const [totalPrice, setTotalPrice] = useState(0);
  const [selectedProductForDevis, setSelectedProductForDevis] = useState(null);
  const navigate = useNavigate();

  // Load cart from localStorage on initial render
  useEffect(() => {
    const savedCart = localStorage.getItem("cartItems");
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  const calculateTotal = useCallback(() => {
    const total = cartItems.reduce((sum, item) => {
      const itemPrice = item.selectedSize?.price || 0;
      return sum + itemPrice * item.quantity;
    }, 0);
    setTotalPrice(total);
  }, [cartItems]); // `cartItems` est la seule dépendance

  // 🔄 Met à jour le localStorage et le total à chaque changement de cartItems
  useEffect(() => {
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    calculateTotal();
  }, [cartItems, calculateTotal]);

  // Add item to cart
  const addToCart = (product) => {
    setCartItems((prevItems) => {
      // Check if this product with the same size already exists in cart
      const existingItemIndex = prevItems.findIndex(
        (item) =>
          item.id === product.id &&
          item.selectedSize?.id === product.selectedSize?.id
      );

      if (existingItemIndex >= 0) {
        // If product already exists, increase quantity
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex].quantity += 1;
        return updatedItems;
      } else {
        // Otherwise, add new item
        return [...prevItems, { ...product, quantity: 1 }];
      }
    });
  };

  // Update quantity of an item
  const updateQuantity = (productId, newQuantity, selectedSize) => {
    setCartItems((prevItems) => {
      if (newQuantity <= 0) {
        // Remove item if quantity is 0 or negative
        return prevItems.filter(
          (item) =>
            !(
              item.id === productId &&
              item.selectedSize?.id === selectedSize?.id
            )
        );
      }

      // Otherwise update quantity
      return prevItems.map((item) => {
        if (
          item.id === productId &&
          item.selectedSize?.id === selectedSize?.id
        ) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
    });
  };

  // Remove item from cart
  const removeFromCart = (productId, selectedSize) => {
    setCartItems((prevItems) =>
      prevItems.filter(
        (item) =>
          !(item.id === productId && item.selectedSize?.id === selectedSize?.id)
      )
    );
  };

  // Clear cart
  const clearCart = () => {
    setCartItems([]);
  };

  // Proceed to DevisPage with all cart items
  const proceedAllToDevis = () => {
    // Create product info for all items
    const productInfo = {
      id: "multiple",
      name: `${cartItems.length} produits`,
      quantity: cartItems.reduce((sum, item) => sum + item.quantity, 0),
      items: cartItems,
      price: totalPrice,
    };

    // Store in localStorage for persistence
    localStorage.setItem("devisProduct", JSON.stringify(productInfo));

    // Set the selected product for devis
    setSelectedProductForDevis(productInfo);

    // Navigate to devis page
    navigate("/devis");
  };

  // Proceed to devis with single product
  const proceedToDevis = (product) => {
    // Store in localStorage for persistence
    localStorage.setItem("devisProduct", JSON.stringify(product));

    // Set the selected product for devis
    setSelectedProductForDevis(product);

    // Navigate to devis page
    navigate("/devis");
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        totalPrice,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        proceedAllToDevis,
        proceedToDevis,
        selectedProductForDevis,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
